import { HQ } from "./sportConfig";
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useSearchParams } from "react-router-dom";
import MatchList from "./components/MatchList";
import Lineup from "./components/LineUp";
import PullToRefresh from "./components/PullToRefresh";
import TipPage from "./components/TipPage";
import { FavoriteTeamsProvider } from "./FavoriteTeams";
import ApiUsage from "./components/ApiUsage";
import UpcomingMatches from "./components/UpcomingMatches";
import LeagueStandings from "./components/LeagueStandings";
import FootballMatch from "./sports/FootballMatch";
import TeamPage from "./components/TeamPage";

const formatLocalDate = (value = new Date()) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeInstalledAppLaunch = () => {
  const standalone = window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
  const navigationEntry = window.performance?.getEntriesByType?.("navigation")?.[0];
  const isReload = navigationEntry
    ? navigationEntry.type === "reload"
    : window.performance?.navigation?.type === 1;

  if (!standalone || isReload || window.location.pathname !== `${HQ.basename}/`) return;

  const params = new URLSearchParams(window.location.search);
  if (!params.has("date")) return;

  params.delete("date");
  const query = params.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
};

normalizeInstalledAppLaunch();

function MatchFetcher() {
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date") || formatLocalDate();
  const [matches, setMatches] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [error, setError] = useState(false);
  const retryTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const forceRefresh = window.sessionStorage.getItem(`${HQ.storagePrefix}-force-refresh`) === "1";
    window.sessionStorage.removeItem(`${HQ.storagePrefix}-force-refresh`);
    const load = async (background = false, fresh = false) => {
      if (!background) setMatches(null);
      try {
        const response = await fetch(`${HQ.dataApi}/matches?date=${encodeURIComponent(date)}${fresh ? "&fresh=true&verify=true" : ""}`, {
          signal: controller.signal,
          cache: fresh ? "no-store" : "default",
        });
        if (!response.ok) throw new Error("Nätverksfel");
        const data = await response.json();
        if (cancelled) return;
        setMatches(data.matches || []);
        setCacheInfo(data);
        setError(false);
        if (data.refreshing) {
          clearTimeout(retryTimer.current);
          retryTimer.current = setTimeout(() => load(true), 2000);
        }
      } catch (fetchError) {
        if (fetchError.name !== "AbortError" && !cancelled) {
          console.error("Fel vid hämtning av matcher:", fetchError);
          setError(true);
        }
      }
    };

    load(false, forceRefresh);
    const interval = date === formatLocalDate() ? setInterval(() => { if (HQ.sport !== "football" || !document.hidden) load(true); }, 60000) : null;
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(retryTimer.current);
      if (interval) clearInterval(interval);
    };
  }, [date]);

  return <MatchList matches={matches} cacheInfo={cacheInfo} error={error} />;
}

function App() {
  return (
    <Router basename={HQ.basename}>
      <FavoriteTeamsProvider>
      <PullToRefresh />
      <Routes>
        <Route path="/" element={<MatchFetcher />} />
        <Route path="/tips" element={<TipPage />} />
        <Route path="/lineup" element={HQ.sport === "football" ? <FootballMatch /> : <Lineup />} />
        <Route path="/match" element={HQ.sport === "football" ? <FootballMatch /> : <Lineup />} />
        <Route path="/admin" element={<ApiUsage />} />
        <Route path="/upcoming" element={<UpcomingMatches />} />
        <Route path="/standings" element={<LeagueStandings />} />
        <Route path="/series" element={<LeagueStandings />} />
        <Route path="/team/:teamId" element={<TeamPage />} />
      </Routes>
      </FavoriteTeamsProvider>
    </Router>
  );
}

export default App;
